import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import type { Row } from "../types";
import { ordenarItens } from "../ordem";
import ErrorState from "./ErrorState";
import { IconPlus, IconClose, IconTrash, IconEye, IconCheck } from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * RECEBIMENTO DE MATERIAIS — a entrada com conferência.
 *
 * O material chega, é registrado com a nota e as fotos, e fica PENDENTE: o
 * estoque só muda quando alguém CONFERE. É esse intervalo que o almoxarifado
 * vive na prática — a nota diz 10, chegam 8, uma caixa vem avariada — e é por
 * isso que a entrada direta em Movimentações não dava conta.
 *
 * Na conferência o sistema dá entrada pela quantidade RECEBIDA (nunca pela da
 * nota) e marca o recebimento como Divergente quando os dois números não batem.
 */

interface RecebimentoLista extends Row {
  id: number;
  nota_fiscal: string | null;
  fornecedor: string | null;
  data_recebimento: string | null;
  data_conferencia: string | null;
  recebido_por: string | null;
  conferido_por: string | null;
  status: string;
  total_itens: number;
  divergencias: number;
  total_fotos: number;
}

const PILL: Record<string, string> = {
  Pendente: "pill-warn",
  Conferido: "pill-ok",
  Divergente: "pill-danger",
  Cancelado: "pill-neutral",
};

/** "2026-08-06" -> "06/08/2026". */
function fmtData(s: string | null): string {
  if (!s) return "—";
  const d = String(s).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split("-").reverse().join("/") : d;
}

function hojeLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Compacta a foto no aparelho (o mesmo tratamento do termo de entrega). */
function compressImage(file: File, max = 1280, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Arquivo de imagem inválido."));
      img.onload = () => {
        const escala = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * escala);
        canvas.height = Math.round(img.height * escala);
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Não foi possível processar a imagem."));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

interface Props {
  itens: Row[];
  user: string;
  /** Nome de exibição do usuário logado (pode não estar carregado ainda). */
  displayName: string | null;
  onDataChanged: () => void;
}

export default function RecebimentosView({ itens, user, displayName, onDataChanged }: Props) {
  // Cancelar o recebimento APAGA o registro e desfaz a entrada: só admin.
  const podeExcluir = usePodeExcluir();
  const [rows, setRows] = useState<RecebimentoLista[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [novo, setNovo] = useState(false);
  const [ver, setVer] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows((await api.recebimentos.list()) as RecebimentoLista[]);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function conferir(r: RecebimentoLista) {
    if (
      !confirm(
        `Conferir o recebimento #${r.id}?\n\n` +
          "O estoque recebe a ENTRADA de cada item pela quantidade que chegou.",
      )
    ) {
      return;
    }
    try {
      const resp = await api.recebimentos.conferir(r.id, { conferido_por: displayName || user });
      setAviso(resp.mensagem);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao conferir o recebimento.");
    }
  }

  async function excluir(r: RecebimentoLista) {
    if (
      !confirm(
        `Cancelar o recebimento #${r.id}?\n\n` +
          "Se ele já foi conferido, as entradas geradas no estoque são desfeitas.",
      )
    ) {
      return;
    }
    try {
      await api.recebimentos.remove(r.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao cancelar.");
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div>
          <span className="badge">{rows.length} recebimento(s)</span>{" "}
          <span className="badge">{rows.filter((r) => r.status === "Pendente").length} pendente(s)</span>{" "}
          <span className="badge">{rows.filter((r) => r.status === "Divergente").length} com divergência</span>
        </div>
        <div className="toolbar-right">
          <button className="btn-primary" onClick={() => setNovo(true)}>
            <IconPlus size={17} /> Registrar recebimento
          </button>
        </div>
      </div>

      {aviso && (
        <div className="receb-aviso">
          {aviso}
          <button className="btn-secondary btn-sm" onClick={() => setAviso(null)}>
            Fechar
          </button>
        </div>
      )}

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            Nenhum material recebido ainda. Use <strong>Registrar recebimento</strong> quando a
            mercadoria chegar — o estoque só é movimentado depois da conferência.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nota fiscal</th>
                <th>Fornecedor</th>
                <th>Recebido em</th>
                <th>Itens</th>
                <th>Conferido em</th>
                <th>Status</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.nota_fiscal || "—"}</td>
                  <td>{r.fornecedor || "—"}</td>
                  <td>{fmtData(r.data_recebimento)}</td>
                  <td>
                    {r.total_itens}
                    {r.divergencias > 0 && (
                      <small className="muted"> · {r.divergencias} divergente(s)</small>
                    )}
                  </td>
                  <td>{fmtData(r.data_conferencia)}</td>
                  <td>
                    <span className={`pill ${PILL[r.status] ?? "pill-neutral"}`}>{r.status}</span>
                  </td>
                  <td className="col-actions">
                    <div className="doc-acoes">
                      <button className="icon-btn" title="Ver recebimento" onClick={() => setVer(r.id)}>
                        <IconEye size={17} />
                      </button>
                      {r.status !== "Conferido" && r.status !== "Cancelado" && (
                        <button className="btn-secondary btn-sm" data-grava onClick={() => conferir(r)}>
                          <IconCheck size={15} /> Conferir
                        </button>
                      )}
                      {podeExcluir && (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Cancelar recebimento"
                          onClick={() => excluir(r)}
                        >
                          <IconTrash size={17} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {novo && (
        <NovoRecebimentoModal
          itens={itens}
          responsavelPadrao={displayName || user}
          onClose={() => setNovo(false)}
          onSaved={async () => {
            setNovo(false);
            await load();
            onDataChanged();
          }}
        />
      )}

      {ver != null && <VerRecebimentoModal id={ver} onClose={() => setVer(null)} />}
    </div>
  );
}

/* ==================== Modal: registrar recebimento ==================== */

interface LinhaItem {
  item_id: string;
  descricao: string;
  quantidade_nota: string;
  quantidade_recebida: string;
  valor_unitario: string;
  observacao: string;
}

const LINHA_VAZIA: LinhaItem = {
  item_id: "",
  descricao: "",
  quantidade_nota: "",
  quantidade_recebida: "",
  valor_unitario: "",
  observacao: "",
};

function NovoRecebimentoModal({
  itens,
  responsavelPadrao,
  onClose,
  onSaved,
}: {
  itens: Row[];
  responsavelPadrao: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [notaFiscal, setNotaFiscal] = useState("");
  const [fornecedor, setFornecedor] = useState("");
  const [data, setData] = useState(hojeLocal());
  const [recebidoPor, setRecebidoPor] = useState(responsavelPadrao);
  const [observacao, setObservacao] = useState("");
  const [linhas, setLinhas] = useState<LinhaItem[]>([{ ...LINHA_VAZIA }]);
  const [fotos, setFotos] = useState<{ imagem: string; legenda: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Select "Item do estoque" em ordem alfabética pela descrição (cópia; `itens` intacto).
  const itensOrdenados = useMemo(() => ordenarItens(itens), [itens]);

  /** Resumo do que esta sendo lancado - o conferente ve ANTES de salvar. */
  const resumo = linhas.reduce(
    (acc, l) => {
      if (!l.item_id && !l.descricao.trim()) return acc;
      const nota = Number(String(l.quantidade_nota).replace(",", ".")) || 0;
      const recebido = Number(String(l.quantidade_recebida || l.quantidade_nota).replace(",", ".")) || 0;
      const valor = Number(String(l.valor_unitario).replace(",", ".")) || 0;
      return {
        itens: acc.itens + 1,
        total: acc.total + recebido * valor,
        divergencias: acc.divergencias + (recebido !== nota ? 1 : 0),
      };
    },
    { itens: 0, total: 0, divergencias: 0 },
  );

  /** Diferenca entre o que a nota diz e o que chegou (por linha). */
  function divergenciaDaLinha(l: LinhaItem): number {
    const nota = Number(String(l.quantidade_nota).replace(",", ".")) || 0;
    const recebido = Number(String(l.quantidade_recebida || l.quantidade_nota).replace(",", ".")) || 0;
    return Math.round((recebido - nota) * 100) / 100;
  }

  function alterar(i: number, campo: keyof LinhaItem, valor: string) {
    setLinhas((atual) => {
      const copia = [...atual];
      copia[i] = { ...copia[i], [campo]: valor };
      // Quantidade recebida em branco assume a da nota: o caso comum é chegar
      // tudo certo, e digitar o mesmo número duas vezes só cansa quem confere.
      if (campo === "quantidade_nota" && !copia[i].quantidade_recebida) {
        copia[i].quantidade_recebida = valor;
      }
      return copia;
    });
  }

  async function adicionarFotos(files: FileList | null) {
    const arquivos = Array.from(files ?? []);
    if (!arquivos.length) return;
    try {
      const novas: { imagem: string; legenda: string }[] = [];
      for (const f of arquivos) {
        if (fotos.length + novas.length >= 12) break;
        novas.push({ imagem: await compressImage(f), legenda: "" });
      }
      setFotos([...fotos, ...novas]);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao processar a foto.");
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    const preenchidas = linhas.filter((l) => l.item_id || l.descricao.trim());
    if (preenchidas.length === 0) return setErro("Informe ao menos um item recebido.");

    setSaving(true);
    try {
      await api.recebimentos.create({
        nota_fiscal: notaFiscal.trim(),
        fornecedor: fornecedor.trim(),
        data_recebimento: data,
        recebido_por: recebidoPor.trim(),
        observacao: observacao.trim(),
        itens: preenchidas.map((l) => ({
          item_id: l.item_id ? Number(l.item_id) : null,
          descricao: l.descricao.trim(),
          quantidade_nota: l.quantidade_nota,
          quantidade_recebida: l.quantidade_recebida || l.quantidade_nota,
          valor_unitario: l.valor_unitario,
          observacao: l.observacao.trim(),
        })),
        fotos,
      });
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao registrar o recebimento.");
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-doc" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Registrar recebimento de material</h3>
            <p className="modal-sub">
              O material fica <strong>Pendente</strong> — o estoque so se mexe quando alguem conferir.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <form className="modal-body doc-form" onSubmit={submit}>
          <div className="doc-grid">
            <label className="field">
              <span>Nota fiscal</span>
              <input value={notaFiscal} onChange={(e) => setNotaFiscal(e.target.value)} placeholder="Nº da NF" />
            </label>
            <label className="field">
              <span>Fornecedor</span>
              <input value={fornecedor} onChange={(e) => setFornecedor(e.target.value)} />
            </label>
            <label className="field">
              <span>Data do recebimento</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
            <label className="field">
              <span>Recebido por</span>
              <input value={recebidoPor} onChange={(e) => setRecebidoPor(e.target.value)} />
            </label>
          </div>

          <div className="field field-wide">
            <span>
              Itens recebidos <span className="req">*</span>{" "}
              <small className="muted">
                (o estoque entra pela quantidade RECEBIDA, na conferencia)
              </small>
            </span>

            {itens.length === 0 && (
              <div className="receb-dica">
                Nenhum item cadastrado no estoque ainda. Da para registrar assim mesmo, escrevendo a{" "}
                <strong>descricao da nota</strong> — mas item fora do cadastro{" "}
                <strong>nao movimenta estoque</strong> na conferencia. Cadastre em{" "}
                <strong>Itens</strong> para o saldo entrar.
              </div>
            )}

            <div className="receb-itens">
              {/* Os titulos ficam FORA dos campos: placeholder some quando a
                  pessoa digita, e ai nao da mais para saber qual coluna e qual. */}
              <div className="receb-linha receb-cabecalho">
                <span>Item do estoque</span>
                <span>Descricao na nota</span>
                <span>Qtd. nota</span>
                <span>Qtd. recebida</span>
                <span>Valor un.</span>
                <span />
              </div>

              {linhas.map((l, i) => {
                const item = itens.find((it) => String(it.id) === l.item_id);
                const diferenca = divergenciaDaLinha(l);
                const preenchida = !!(l.item_id || l.descricao.trim());
                return (
                  <div key={i} className="receb-linha">
                    <select
                      value={l.item_id}
                      onChange={(e) => alterar(i, "item_id", e.target.value)}
                      title="Item do estoque"
                    >
                      <option value="">— Item fora do cadastro —</option>
                      {itensOrdenados.map((it) => (
                        <option key={it.id} value={it.id}>
                          {it.codigo} — {it.descricao}
                        </option>
                      ))}
                    </select>

                    {l.item_id ? (
                      <div className="receb-item-info">
                        {String(item?.descricao ?? "")}
                        {item?.unidade ? <small className="muted"> · {String(item.unidade)}</small> : null}
                        {item?.saldo != null && (
                          <small className="muted"> · saldo {String(item.saldo)}</small>
                        )}
                      </div>
                    ) : (
                      <input
                        placeholder="Como esta escrito na nota"
                        value={l.descricao}
                        onChange={(e) => alterar(i, "descricao", e.target.value)}
                      />
                    )}

                    <input
                      type="number"
                      step="any"
                      min="0"
                      placeholder="0"
                      title="Quantidade que a nota fiscal informa"
                      value={l.quantidade_nota}
                      onChange={(e) => alterar(i, "quantidade_nota", e.target.value)}
                    />

                    <div className="receb-recebido">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        placeholder="0"
                        title="Quantidade que realmente chegou"
                        value={l.quantidade_recebida}
                        onChange={(e) => alterar(i, "quantidade_recebida", e.target.value)}
                      />
                      {/* A diferenca aparece na hora: e o que o conferente
                          precisa ver antes de salvar, nao depois. */}
                      {preenchida && diferenca !== 0 && (
                        <span className="receb-diferenca" title="Diferenca entre o recebido e a nota">
                          {diferenca > 0 ? `+${diferenca}` : diferenca}
                        </span>
                      )}
                    </div>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      title="Valor unitario (opcional)"
                      value={l.valor_unitario}
                      onChange={(e) => alterar(i, "valor_unitario", e.target.value)}
                    />

                    <button
                      type="button"
                      className="icon-btn icon-btn-danger"
                      title="Remover item"
                      onClick={() => setLinhas(linhas.filter((_, x) => x !== i))}
                      disabled={linhas.length === 1}
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="receb-rodape-itens">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setLinhas([...linhas, { ...LINHA_VAZIA }])}
              >
                <IconPlus size={15} /> Adicionar item
              </button>
              <span className="receb-resumo">
                {resumo.itens} item(ns)
                {resumo.total > 0 && (
                  <>
                    {" · "}
                    <strong>
                      {resumo.total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </strong>
                  </>
                )}
                {resumo.divergencias > 0 && (
                  <span className="receb-alerta">
                    {" · "}
                    {resumo.divergencias} com diferenca da nota
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className="field field-wide">
            <span>
              Fotos do recebimento <small className="muted">(nota, embalagem, avaria — máx. 12)</small>
            </span>
            {fotos.length > 0 && (
              <div className="galeria-fotos">
                {fotos.map((f, i) => (
                  <div key={i} className="galeria-item">
                    <img src={f.imagem} alt={`Foto ${i + 1}`} />
                    <input
                      className="galeria-legenda"
                      placeholder="Legenda"
                      value={f.legenda}
                      onChange={(e) => {
                        const copia = [...fotos];
                        copia[i] = { ...copia[i], legenda: e.target.value };
                        setFotos(copia);
                      }}
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-sm btn-danger-text"
                      onClick={() => setFotos(fotos.filter((_, x) => x !== i))}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="photo-btn" style={{ display: "block", textAlign: "center", cursor: "pointer" }}>
              📷 Adicionar fotos
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                hidden
                onChange={(e) => {
                  adicionarFotos(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
          </div>

          <label className="field field-wide">
            <span>Observação</span>
            <textarea rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </label>

          {erro && <div className="form-error">{erro}</div>}

          <div className="modal-foot">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Salvando..." : "Registrar recebimento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ==================== Modal: ver recebimento ==================== */

function VerRecebimentoModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [dados, setDados] = useState<Row | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.recebimentos
      .get(id)
      .then(setDados)
      .catch((e) => setErro(e instanceof Error ? e.message : "Erro ao carregar."));
  }, [id]);

  const itens = (dados?.itens ?? []) as Row[];
  const fotos = (dados?.fotos ?? []) as Row[];

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-doc" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Recebimento #{id}</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          {erro && <div className="form-error">{erro}</div>}
          {!dados && !erro && <div className="empty">Carregando…</div>}

          {dados && (
            <>
              <div className="doc-grid">
                <div className="field">
                  <span>Nota fiscal</span>
                  <strong>{dados.nota_fiscal || "—"}</strong>
                </div>
                <div className="field">
                  <span>Fornecedor</span>
                  <strong>{dados.fornecedor || "—"}</strong>
                </div>
                <div className="field">
                  <span>Recebido em</span>
                  <strong>{fmtData(dados.data_recebimento as string)}</strong>
                </div>
                <div className="field">
                  <span>Recebido por</span>
                  <strong>{dados.recebido_por || "—"}</strong>
                </div>
                <div className="field">
                  <span>Conferido em</span>
                  <strong>{fmtData(dados.data_conferencia as string)}</strong>
                </div>
                <div className="field">
                  <span>Conferido por</span>
                  <strong>{dados.conferido_por || "—"}</strong>
                </div>
              </div>

              <table className="table" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th style={{ textAlign: "right" }}>Nota</th>
                    <th style={{ textAlign: "right" }}>Recebido</th>
                    <th style={{ textAlign: "right" }}>Diferença</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((i) => (
                    <tr key={String(i.id)}>
                      <td>
                        {(i.item_descricao as string) || (i.descricao as string) || "—"}
                        {!i.item_id && <small className="muted"> (fora do cadastro)</small>}
                      </td>
                      <td style={{ textAlign: "right" }}>{Number(i.quantidade_nota)}</td>
                      <td style={{ textAlign: "right" }}>{Number(i.quantidade_recebida)}</td>
                      <td style={{ textAlign: "right" }}>
                        {Number(i.divergencia) === 0 ? (
                          "—"
                        ) : (
                          <span className="pill pill-danger">{Number(i.divergencia)}</span>
                        )}
                      </td>
                      <td>{(i.observacao as string) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {dados.observacao ? (
                <p className="muted" style={{ marginTop: 10 }}>
                  <strong>Observação:</strong> {String(dados.observacao)}
                </p>
              ) : null}

              {fotos.length > 0 && (
                <div className="galeria-fotos" style={{ marginTop: 12 }}>
                  {fotos.map((f) => (
                    <div key={String(f.id)} className="galeria-item">
                      <img src={String(f.imagem)} alt={String(f.legenda ?? "Foto")} />
                      <small className="muted">{String(f.legenda ?? "")}</small>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn-primary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
